import { useCallback, useMemo } from 'react';
import { Paths, ProjectPathParams } from '../router/app.router';
import { NavigationBar } from '../../ui/components/pure/navigation-bar/navigation-bar.component';
import {
  BuildingSelectProject,
  sortProjectsByHierarchy
} from '../../ui/components/pure/building-select/building-select.component';
import { useQueryListProjects } from '../../core/queries/projects.queries';
import { useHistory, useLocation } from 'react-router-dom';
import { useNavigationItems } from './use-navigation-items.hook';
import { useAuth } from '@workos-inc/authkit-react';
import { useAuthActions } from '../../core/workos/auth-actions.hook';

// TODO: Fix global pages and add this back in
//type CurrentStateGlobal = { type: 'global' }
type CurrentSelectState = { type: 'project'; projectID: string; shortcode?: string }; // | CurrentStateGlobal

export function NavigationBarContainer({ params }: { params?: Partial<ProjectPathParams<Paths.project>> }) {
  const { user } = useAuth();
  const { signOut } = useAuthActions();
  const projectsResult = useQueryListProjects();

  const location = useLocation();
  const history = useHistory();
  const items = useNavigationItems(params);

  const projectOptions: BuildingSelectProject[] | undefined = useMemo(
    () => (projectsResult.data ? sortProjectsByHierarchy(projectsResult.data) : []),
    [projectsResult.data]
  );

  const iconClick = () => {
    history.push('/');
  };

  const handleProjectChange = useCallback(
    (state: CurrentSelectState) => {
      // early exit if projectId hasn't changed
      if (
        params?.projectID === state.projectID ||
        (typeof params?.shortcode !== 'undefined' && params.shortcode === state.shortcode)
      ) {
        return;
      }

      let newPath = `/projects/${state.shortcode || state.projectID}`;
      let newSearch = location.search;

      // Try to keep current page if already in a project
      if (params?.projectID !== undefined) {
        const parts = location.pathname.split('/');
        const projectSubPath = parts[3] || '';
        const additionalPathPart = parts[4] || '';

        if (projectSubPath.length) {
          newPath += `/${projectSubPath}`;
        }

        // The additionalPathPart will be the uuid of an entity if the previous page was a details page
        // If it is defined, we should remove any query params from the URL because they will not be relevant
        // to the list page we are redirecting to
        if (additionalPathPart.length) {
          newSearch = '';
        }
      }

      const path = {
        ...location,
        pathname: newPath,
        search: newSearch,
        state: {
          projectSwitch: true
        }
      };

      history.push(path);
    },
    [history, location, params?.projectID, params?.shortcode]
  );

  return (
    <NavigationBar
      items={items}
      iconClick={iconClick}
      user={user ? { username: `${user.firstName} ${user.lastName}`, onLogout: signOut } : undefined}
      buildingSelect={
        isProjectParams(params)
          ? {
              currentState: { type: 'project', ...params },
              isLoading: projectsResult.isLoading,
              onStateChange: handleProjectChange,
              projects: projectOptions
            }
          : undefined
      }
    />
  );
}

function isProjectParams(
  params?: Partial<ProjectPathParams<Paths.project>>
): params is ProjectPathParams<Paths.project> {
  return !!(params && params.projectID);
}
